import { React, useEffect, useState } from 'react'
import { Card } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { colors, breakpoints } from 'globalStyles'
import SVGIcon from 'reusableComponents/SVGIcon'
import { ReactComponent as DelInstructions } from 'assets/svg/delivery-instructions.svg'
import { ReactComponent as Pencil } from 'assets/svg/pencil.svg'
import PropTypes from 'prop-types'
import InputField from 'reusableComponents/InputField/InputField'
import { getUserPreferencesInfo } from 'store/recipient/recipientSlice'
import Form from 'react-bootstrap/Form'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Button from 'react-bootstrap/Button'
import SuccessModal from 'components/SuccessModal'
import { updateUserPreferences } from 'api/accounts'
import CheckMark from 'reusableComponents/CheckMark'
import {
  pencilIconStyle,
  tableHeaderStyle,
  tableColumnStyle,
  tableContentStyle,
  tableSubContentStyle,
  deliveryInstructionsIconStyle,
  StyledCard,
} from './MyPreferences.style'

const pywBackendURL = process.env.REACT_APP_PYW_BACKEND_API

const StyledCardTitle = styled(Card.Title)`
  text-align: left;
  width: 98%;

  @media ${breakpoints.phone} {
    text-align: center;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 20px;
  width: 100%;
  box-sizing: border-box;

  button {
    flex: 1;
  }
`

const RedInputCount = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  padding: 10px 14px 0 0;
  font-size: 10px;
  color: ${colors.primary_red};
  pointer-events: none;
`

const InputWrapper = styled.div`
  position: relative;
`

const MAX_DELIVERY_INSTRUCTIONS_LENGTH = 150

const DeliveryInstructionsPreference = ({ userPreferenceDetails }) => {
  const prn = pywBackendURL
  const { t, i18n } = useTranslation()
  const { lang } = useParams()
  const dispatch = useDispatch()
  const { language } = i18n
  const [disableSaveButton, setDisableSaveButton] = useState(true)
  const [inputCount, setInputCount] = useState(0)
  const [isEditingDeliveryInstructionsPreference, setIsEditingDeliveryInstructionsPreference] =
    useState(false)
  const [showGreenCheck, setShowGreenCheck] = useState(false)
  const [deliveryInstructionsPreference, setDeliveryInstructionsPreference] = useState(
    userPreferenceDetails.additionalDeliveryInstructions
  )
  const [saveDeliveryInstructionsPreference, setSaveDeliveryInstructionsPreference] =
    useState(false)
  const [showModal, setShowModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const originalDeliveryInstructions = userPreferenceDetails.additionalDeliveryInstructions || ''

  const deliveryInstructions = deliveryInstructionsPreference || t(`my_preferences.none_selected`)

  const deliveryInstructionsChangeHandler = (event) => {
    const { value } = event.target
    setDeliveryInstructionsPreference(value)
    setInputCount(value.length)
    setDisableSaveButton(value === originalDeliveryInstructions)
  }

  const onClickCancel = () => {
    setDeliveryInstructionsPreference(originalDeliveryInstructions)
    setInputCount(originalDeliveryInstructions.length)
    setDisableSaveButton(true)
    setIsEditingDeliveryInstructionsPreference(false)
  }

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang)
    }
    if (saveDeliveryInstructionsPreference) dispatch(getUserPreferencesInfo(prn))
  }, [lang, i18n, language, saveDeliveryInstructionsPreference, dispatch, prn])

  const editDeliveryInstructionsPreference = () => {
    setDeliveryInstructionsPreference(originalDeliveryInstructions)
    setInputCount(originalDeliveryInstructions.length)
    setDisableSaveButton(true)
    setIsEditingDeliveryInstructionsPreference(true)
  }

  const cancelModal = () => {
    setShowModal(false)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    const hadInstructions = Boolean(originalDeliveryInstructions?.trim())
    const hasInstructionsNow = Boolean(deliveryInstructionsPreference?.trim())

    const message =
      !hadInstructions && hasInstructionsNow
        ? t('my_preferences.successfully_added_instructions')
        : t('my_preferences.successfully_updated_instructions')

    try {
      await updateUserPreferences('additionalDeliveryInstructions', deliveryInstructionsPreference)
      setSaveDeliveryInstructionsPreference(true)
      setSuccessMessage(message)
      setShowModal(true)
      setTimeout(() => {
        setShowModal(false)
        setShowGreenCheck(true)
      }, 3000)

      setIsEditingDeliveryInstructionsPreference(false)
      setDisableSaveButton(true)
    } catch (err) {
      setSaveDeliveryInstructionsPreference(false)
      const fallbackError = t('my_preferences.error')
      setSuccessMessage(err?.message || fallbackError)
      setShowModal(true)
    }
  }
  const handleCheckMarkComplete = () => {
    setShowGreenCheck(false)
  }
  return (
    <StyledCard style={{ marginTop: '24px' }}>
      <Card.Header
        style={{
          backgroundColor: isEditingDeliveryInstructionsPreference
            ? `${colors.sky_blue}`
            : 'inherit',
          height: 'auto',
          minHeight: '72px',
        }}
      >
        <div style={{ display: 'table', width: '100%' }}>
          <div style={tableHeaderStyle}>
            <SVGIcon
              Icon={DelInstructions}
              size="lg"
              aria-label="Additional Delivery Instructions Preference"
              style={deliveryInstructionsIconStyle}
            />
          </div>
          <div style={{ display: 'table-cell' }}>
            <div style={tableContentStyle}>{t(`my_preferences.add_del_instructions`)}</div>
            <div
              style={{
                ...tableSubContentStyle,
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                paddingRight: '8px',
              }}
            >
              {deliveryInstructions}
            </div>
          </div>
          <div style={tableColumnStyle}>
            {!isEditingDeliveryInstructionsPreference && !showGreenCheck && (
              <SVGIcon
                Icon={Pencil}
                size="lg"
                color={colors.primary_blue}
                aria-label="Edit delivery instructions preference"
                onClick={editDeliveryInstructionsPreference}
                style={pencilIconStyle}
              />
            )}
            {!isEditingDeliveryInstructionsPreference && showGreenCheck && (
              <CheckMark
                color={colors.primary_green}
                size="lg"
                delay="10000"
                onComplete={handleCheckMarkComplete}
              />
            )}
          </div>
        </div>
      </Card.Header>
      <Form id="saveDeliveryInstructionsForm" onSubmit={handleSubmit}>
        {isEditingDeliveryInstructionsPreference && (
          <Card.Body>
            <StyledCardTitle>{t(`my_preferences.del_instructions`)}</StyledCardTitle>
            <InputWrapper>
              <FloatingLabel id="deliveryInstructions">
                <InputField
                  id="additional-delivery-instructions"
                  inputCount={0}
                  onChange={deliveryInstructionsChangeHandler}
                  value={deliveryInstructionsPreference || ''}
                  maxLength={MAX_DELIVERY_INSTRUCTIONS_LENGTH}
                  label={t('my_preferences.add_del_instructions_here')}
                  as="textarea"
                  rows={7}
                  inputWidth="100%"
                  style={{ height: '150px', width: '100%' }}
                />
              </FloatingLabel>
              {inputCount > 0 && (
                <RedInputCount>
                  {inputCount}/{MAX_DELIVERY_INSTRUCTIONS_LENGTH}
                </RedInputCount>
              )}
            </InputWrapper>
            <ButtonContainer>
              <Button type="button" variant="secondary" onClick={onClickCancel}>
                {t('buttonActions.cancel')}
              </Button>
              <Button
                type="submit"
                form="saveDeliveryInstructionsForm"
                disabled={disableSaveButton}
              >
                {t('buttonActions.save')}
              </Button>
            </ButtonContainer>
          </Card.Body>
        )}
        <SuccessModal show={showModal} onHide={cancelModal} message={successMessage} />
      </Form>
    </StyledCard>
  )
}

DeliveryInstructionsPreference.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  userPreferenceDetails: PropTypes.object.isRequired,
}

export default DeliveryInstructionsPreference
